/**
 * lx-tab-bar —— 底部导航栏
 *
 * 三个 tab：首页 / 联系人 / 设置。点击广播 ui:tab-changed。
 * 图标用内联 SVG（无外部依赖，可随主题变色）。
 */

import { LxElement, define } from '@/core/base-element';
import type { TabKey } from '@/types/events';

interface TabDef {
  key: TabKey;
  label: string;
  icon: string;
}

/** 图标为 24×24 视框的 path，stroke 型，随 currentColor 变色 */
const TABS: TabDef[] = [
  {
    key: 'home',
    label: '首页',
    icon: '<path d="M4 5.5h16M4 12h16M4 18.5h10" stroke-linecap="round"/>',
  },
  {
    key: 'contacts',
    label: '联系人',
    icon: '<circle cx="12" cy="8" r="3.2"/><path d="M5 19.5c0-3.4 3.1-5.6 7-5.6s7 2.2 7 5.6" stroke-linecap="round"/>',
  },
  {
    key: 'settings',
    label: '设置',
    icon: '<circle cx="12" cy="12" r="2.8"/><path d="M12 3.5v2M12 18.5v2M20.5 12h-2M5.5 12h-2M18 6l-1.4 1.4M7.4 16.6 6 18M18 18l-1.4-1.4M7.4 7.4 6 6" stroke-linecap="round"/>',
  },
];

export class LxTabBar extends LxElement {
  static get observedAttributes(): string[] {
    return ['tab'];
  }

  attributeChangedCallback(): void {
    this.update();
  }

  protected styles(): string {
    return `
      :host {
        display: block;
        background: var(--c-surface);
        border-top: 1px solid var(--c-border);
      }

      nav {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        height: var(--layout-tab-bar-h);
        /* 底部安全区（手势条） */
        padding-bottom: env(safe-area-inset-bottom, 0px);
      }

      .tab {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        color: var(--c-fg-muted);
        transition: color var(--dur-fast) var(--ease-standard);
        position: relative;
      }

      .tab:hover {
        color: var(--c-fg);
      }

      .tab[aria-current='page'] {
        color: var(--c-fg);
      }

      /* 选中态：顶部一条荧光色指示条，是主色最合适的使用位置 */
      .tab[aria-current='page']::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 24px;
        height: 3px;
        border-radius: var(--radius-full);
        background: var(--c-accent);
      }

      .tab svg {
        width: 22px;
        height: 22px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.6;
      }

      .label {
        font-size: var(--fs-xs);
        line-height: 1;
      }
    `;
  }

  protected render(): string {
    const active = this.getAttribute('tab') ?? 'home';
    return `
      <nav aria-label="主导航">
        ${TABS.map(
          (t) => `
          <button
            class="tab"
            type="button"
            data-tab="${t.key}"
            ${active === t.key ? 'aria-current="page"' : ''}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">${t.icon}</svg>
            <span class="label">${t.label}</span>
          </button>
        `,
        ).join('')}
      </nav>
    `;
  }

  protected onMounted(): void {
    this.$$('.tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tab = btn.getAttribute('data-tab') as TabKey;
        if (tab === this.getAttribute('tab')) return;
        this.emit('ui:tab-changed', { tab });
      });
    });
  }
}

define('lx-tab-bar', LxTabBar);
